import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { authService } from '../../services/authService.js'
import { usePostHog } from '../usePostHog.js'
import { useListPreferences } from '../useListPreferences.js'
import { useCompetencesReferentiel } from '../formateurs/useCompetencesReferentiel.js'

const isAuthenticated = ref(false)
const user = ref(null)
const isLoading = ref(true)

export function useAuth() {
  const router = useRouter()
  const { identifyUser, resetUser } = usePostHog()
  const { loadPreferences, resetPreferences } = useListPreferences()
  // Précharge le référentiel des compétences (thèmes + sous-compétences) pour que
  // les libellés soient disponibles dans les vues BO sans appel par composant.
  const { loadCompetencesReferentiel } = useCompetencesReferentiel()

  const checkAuth = async () => {
    try {
      const data = await authService.me()

      if (data.authenticated) {
        isAuthenticated.value = true
        user.value = data.user
        isLoading.value = false
        // Identify user in PostHog for session replay
        identifyUser(data.user)
        // Précharge les préférences de listes (nombre par page) avant l'affichage
        // des vues.
        await loadPreferences()
        // Non bloquant : les libellés se remplissent réactivement dès réception.
        loadCompetencesReferentiel()
        return data.user
      } else {
        isAuthenticated.value = false
        user.value = null
        isLoading.value = false
        return false
      }
    } catch (err) {
      console.error('Auth check failed:', err)
      isAuthenticated.value = false
      user.value = null
      isLoading.value = false
      return false
    }
  }

  const login = async (identifier, password) => {
    try {
      const data = await authService.login(identifier, password)
      if (data.success) {
        isAuthenticated.value = true
        user.value = data.user
        // Identify user in PostHog for session replay
        identifyUser(data.user)
        // Charge les préférences de listes du nouvel utilisateur connecté.
        await loadPreferences()
        loadCompetencesReferentiel()
        return { success: true }
      }
      return { success: false, error: data.error || null, status: null }
    } catch (err) {
      console.error('Login failed:', err)
      const apiError = err.response?.data?.error || err.message || null
      // LE STATUT REMONTE AVEC LE REFUS. `login` ne relance jamais : sans lui,
      // l'écran de connexion ne pouvait pas distinguer un mot de passe faux
      // (401) d'une panne ou d'une limite de tentatives (429, 500, 503), et
      // annonçait « Identifiant ou mot de passe incorrect » à quelqu'un qui
      // avait tapé le bon.
      const status = err.status ?? err.response?.status ?? null
      return { success: false, error: apiError, status, cause: err }
    }
  }

  /**
   * Oublie la session côté client, sans rien demander au serveur.
   *
   * Deux appelants : la déconnexion volontaire (après l'appel serveur) et la
   * session expirée détectée sur un 401, où il n'y a plus rien à déconnecter —
   * seul l'état en mémoire ment encore en affichant l'utilisateur connecté.
   */
  const clearSession = () => {
    isAuthenticated.value = false
    user.value = null
    // Reset user identification in PostHog
    resetUser()
    // Réinitialise les préférences de listes en mémoire
    resetPreferences()
  }

  const logout = async () => {
    try {
      await authService.logout()
      clearSession()
      router.push('/login')
    } catch (err) {
      console.error('Logout failed:', err)
    }
  }

  return {
    isAuthenticated,
    user,
    isLoading,
    checkAuth,
    login,
    logout,
    clearSession
  }
}

